// Feature: Auth - Service Layer
// Location: src/features/auth/services/authService.ts

import { axiosInstance } from 'core/api/baseClient';
import type { RegisterData } from 'types/auth.types';
import type { MeResponse, LoginResponse } from 'types/api.generated';

let _refreshPromise: Promise<{ accessToken: string }> | null = null;

export type MePayload = MeResponse;

export interface AuthUserPayload {
  userId: string;
  email: string;
  firstName?: string;
  lastName?: string | null;
  appRole?: 'customer' | 'provider';
  platformRole?: string | null;
}

export type { LoginResponse };

export const authService = {
  login: async (email: string, password: string): Promise<LoginResponse> => {
    const res = await axiosInstance.post<LoginResponse>('/api/v1/auth/login', { email, password });
    return res.data;
  },

  register: async (data: RegisterData): Promise<LoginResponse> => {
    const role = data.role === 'provider' ? 'provider' : 'customer';
    const res = await axiosInstance.post<LoginResponse>('/api/v1/auth/register', {
      email: data.email,
      password: data.password,
      firstName: data.firstName,
      lastName: data.lastName,
      role,
    });
    return res.data;
  },

  logout: async (accessToken: string | null): Promise<void> => {
    await axiosInstance.post(
      '/api/v1/auth/logout',
      undefined,
      accessToken ? { headers: { Authorization: `Bearer ${accessToken}` } } : {},
    );
  },

  refreshToken: (): Promise<{ accessToken: string }> => {
    if (!_refreshPromise) {
      _refreshPromise = axiosInstance
        .post<{ accessToken: string }>('/api/v1/auth/refresh-token', undefined, { withCredentials: true })
        .then((res) => res.data)
        .finally(() => { _refreshPromise = null; });
    }
    return _refreshPromise;
  },

  getMe: async (accessToken: string): Promise<MePayload> => {
    const res = await axiosInstance.get<MePayload>('/api/v1/user/me', {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return res.data;
  },
};
